/**
 * @module ChartSettingsPanel
 * @description 图表设置面板 Shell — 遍历 Config A + 完整数据流
 *
 * 对照渲染方案 §5.2 + 状态管理方案 §5.1 实现：
 *   - Panel 组合 usePanelActions / useSettingsDraft / useQBLinkagePipeline
 *   - 使用 TEA Tabs/TabPanel 组件实现页签切换
 *   - 顶部标题栏 + 关闭按钮
 *   - Confirm/Cancel 按钮
 *   - L5 visibleItems 跨 Tab 预计算
 *   - formDelKey 基于 Config B 计算
 *   - 打开/关闭面板仅变更 chartsModal（编辑页 BASE 由 chartSettingsBridge 初始化；新建页 Confirm 时 COMMIT_DRAFT）
 */

import React, { useMemo, useCallback, useEffect } from 'react';
import { useIntl } from 'umi';
import { Button, Tabs, TabPanel, notification } from 'tea-component';
import PANEL_LAYOUT_BY_CHART, {
  layoutToResolvedGroups,
} from '../manifest/panelLayout';
import { useChartSettingsSelector, useChartSettingsDispatch } from '../context';
import { usePanelActions } from './hooks/usePanelActions';
import { useSettingsDraft } from './hooks/useSettingsDraft';
import { useQBLinkagePipeline } from '../linkage/qbFields/useQBLinkagePipeline';
import { useQuerySelector } from '../hooks/deps';
import { toChartTypeEnum } from '../utils/toChartTypeEnum';
import { CSActionType } from '../types';
import { usePanelQBSnapshot } from './hooks/usePanelQBSnapshot';
import SvgIcon from '@home/components/SvgIcon';
import GroupComponentWrapper from './GroupComponentWrapper';
import { evaluateVisibility } from '../manifest/visibility/evaluateVisibility';
import { validateDualYAxisMetrics } from './utils/validationUtils';
import { EMPTY_COLUMN_INFO_LIST } from '../utils/constants';
import {
  DualYAxisValidationProvider,
  useDualYAxisValidation,
} from './DualYAxisValidationContext';
import './index.scss';

/** Tab ID → i18n key 映射（对应 ChartsDrop 旧代码 dbl_chart_* 前缀） */
const TAB_LABEL_KEYS: Record<string, string> = {
  basic: 'dbl_chart_style',
  advanced: 'dbl_chart_advanced',
  yAxis: 'dbl_chart_y_axis',
};

const ChartSettingsPanelInner: React.FC = () => {
  const { formatMessage } = useIntl();
  const dispatch = useChartSettingsDispatch();
  const { dismissPanel, cancelPanel, switchTab } = usePanelActions();

  const chartTypeStr = useQuerySelector(s => s.chartType as string);
  const columnDimensions = useQuerySelector(
    s => s.columnDimensions ?? EMPTY_COLUMN_INFO_LIST,
  );
  const columnMetrics = useQuerySelector(
    s => s.columnMetrics ?? EMPTY_COLUMN_INFO_LIST,
  );
  const chartType = useMemo(
    () => toChartTypeEnum(chartTypeStr),
    [chartTypeStr],
  );
  const { onConfirm } = useSettingsDraft({
    chartType,
    columnDimensions,
    columnMetrics,
  });
  useQBLinkagePipeline();

  const chartsModal = useChartSettingsSelector((s) => s.chartsModal);
  const activeTabs = useChartSettingsSelector((s) => s.activeTabs);
  const base = useChartSettingsSelector((s) => s.base);
  const draft = useChartSettingsSelector((s) => s.draft);
  const { setErrors: setDualYAxisErrors, clearErrors: clearDualYAxisErrors } =
    useDualYAxisValidation();

  const qbSnapshot = usePanelQBSnapshot();
  const tabs = PANEL_LAYOUT_BY_CHART[chartType];

  /** 打开面板时清除上次双 Y 轴校验错误 */
  useEffect(() => {
    if (chartsModal) {
      clearDualYAxisErrors();
    }
  }, [chartsModal, clearDualYAxisErrors]);

  const effectiveActiveTabs = useMemo(() => {
    if (!tabs || tabs.length === 0) return activeTabs;
    return tabs.some((t) => t.id === activeTabs) ? activeTabs : tabs[0].id;
  }, [tabs, activeTabs]);

  useEffect(() => {
    if (effectiveActiveTabs !== activeTabs) {
      dispatch({ type: CSActionType.SET_ACTIVE_TAB, payload: effectiveActiveTabs });
    }
  }, [effectiveActiveTabs, activeTabs, dispatch]);

  const activePanelTabLayout = tabs?.find(t => t.id === effectiveActiveTabs);

  const tabItems = useMemo(() => {
    if (!tabs) return [];
    return tabs.map((tab) => ({
      id: tab.id,
      label: formatMessage({ id: TAB_LABEL_KEYS[tab.id] || 'dbl_chart_style' }),
    }));
  }, [tabs, formatMessage]);

  const visibility = useMemo(() => {
    if (!tabs || !qbSnapshot) {
      return { groups: {}, items: {}, formDelKey: {} };
    }
    return evaluateVisibility(
      qbSnapshot,
      layoutToResolvedGroups(chartType, tabs),
    );
  }, [qbSnapshot, tabs, chartType]);

  /**
   * 步骤1：双Y轴指标校验
   * @returns true = 校验通过，false = 校验失败（已 dispatch 错误状态）
   */
  const runDualYAxisValidation = useCallback((): boolean => {
    const mergedValues = { ...base, ...draft };
    const errors = validateDualYAxisMetrics(
      mergedValues,
      columnMetrics.length,
    );

    if (errors.primary || errors.secondary) {
      setDualYAxisErrors(errors);
      return false;
    }

    clearDualYAxisErrors();
    return true;
  }, [base, draft, columnMetrics, setDualYAxisErrors, clearDualYAxisErrors]);

  /**
   * 步骤2：提交配置 + 触发重算
   */
  const runSubmitConfig = useCallback(() => {
    const { payload: _payload, needsRecalculation } = onConfirm(
      visibility.formDelKey,
    );
    if (needsRecalculation) {
      dispatch({ type: CSActionType.BUMP_RECALC_TRIGGER });
    }
  }, [onConfirm, visibility.formDelKey, dispatch]);

  /**
   * 步骤3：弹出成功提示（formatMessage 引用稳定，useCallback 有效）
   */
  const runShowSuccess = useCallback(() => {
    notification.success({
      description: formatMessage({ id: 'dbl_chart_config_success' }),
      duration: 3000,
    });
  }, [formatMessage]);

  const handleConfirm = useCallback(() => {
    if (!runDualYAxisValidation()) return;
    runSubmitConfig();
    runShowSuccess();
  }, [runDualYAxisValidation, runSubmitConfig, runShowSuccess]);

  const handleCancel = useCallback(() => {
    cancelPanel();
  }, [cancelPanel]);

  if (!tabs || tabs.length === 0 || !chartsModal) return null;
  if (!activePanelTabLayout) return null;

  return (
    <>
      <div className="dbl-charts-drop-wrap-placeholder"></div>
      <div className="dbl-charts-drop-wrap dbl-charts-drop-wrap-fixed">
        <div className="dbl-charts-container">
          <div className="dbl-charts-container-header">
          <div className="dbl-charts-container-header-text">
            {formatMessage({ id: 'dbl_chart_graph_settings' })}
          </div>
          <SvgIcon
            style={{ width: 14, height: 14 }}
            type="clonse"
            onClick={dismissPanel}
          />
          </div>

          <Tabs
          tabs={tabItems}
          activeId={effectiveActiveTabs}
          onActive={(tab) => {
            switchTab(tab.id);
          }}
        >
          {tabs.map((tab) => (
            <TabPanel key={tab.id} id={tab.id}>
              {effectiveActiveTabs === tab.id ? (
                <div className="dbl-chart-settings-panel__body">
                  {tab.groups
                    .filter(groupId => visibility.groups[groupId])
                    .map(groupId => (
                      <GroupComponentWrapper
                        key={groupId}
                        groupId={groupId}
                        visibleItems={visibility.items[groupId]}
                      />
                    ))}
                </div>
              ) : null}
            </TabPanel>
          ))}
          </Tabs>

          <div className="dbl-charts-container-footer">
          <Button
            type="primary"
            onClick={handleConfirm}
            className="dbl-charts-container-footer-ok"
          >
            {formatMessage({ id: 'dbl_chart_confirm' })}
          </Button>
          <Button type="weak" onClick={handleCancel}>
            {formatMessage({ id: 'dbl_chart_cancel' })}
          </Button>
          </div>
        </div>
      </div>
    </>
  );
};

const ChartSettingsPanel: React.FC = () => (
  <DualYAxisValidationProvider>
    <ChartSettingsPanelInner />
  </DualYAxisValidationProvider>
);

export default React.memo(ChartSettingsPanel);
export { evaluateVisibility } from '../manifest/visibility/evaluateVisibility';
export { validateDualYAxisMetrics } from './utils/validationUtils';
